import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { User } from "@shared/schema";
import { Shield, Users, Eye, MoreHorizontal, Loader2, ToggleLeft, Settings2, Zap, Search, X, UserCheck, SquarePen } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { RoleAccessSheet } from "@/components/admin/RoleAccessSheet";
import { useDebounce } from "@/hooks/use-debounce";
import { TramsAccountManager } from "@/components/trams/TramsAccountManager";
import { useResolvedFeatureFlags } from "@/hooks/useResolvedFeatureFlags";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "wouter";
import { FINANCES_PATHS } from "@/lib/financesPaths";

// Extended user type with RBAC fields from T-366
interface AdminUser extends User {
  roleSlug: string | null;
  threadsAdvancedEnabled: boolean;
  companyName: string | null;
}

// API response shape from GET /api/admin/users
interface UsersApiResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Role labels for display (legacy roles + new RBAC roles)
const roleLabels: Record<string, string> = {
  // New RBAC roles
  SYSTEM_ADMIN: "System Admin",
  AGENCY_OWNER: "Agency Owner",
  AGENCY_MANAGER: "Agency Manager",
  ADVISOR: "Associate Advisor",
  READ_ONLY: "Read Only",
  // Legacy roles (for backward compatibility)
  ADMIN: "Admin", 
  WORLDVIA_HQ: "WorldVia HQ",
  WORLDVIA_ADVISOR: "WorldVia Advisor",
  CLIENT: "Client",
  PUBLIC_PRO_USER: "Pro User",
  PUBLIC_FREE_USER: "Free User"
};

const roleBadgeVariants: Record<string, "destructive" | "secondary" | "default" | "outline"> = {
  SYSTEM_ADMIN: "destructive",
  AGENCY_OWNER: "default",
  AGENCY_MANAGER: "default",
  ADVISOR: "secondary",
  READ_ONLY: "outline",
  ADMIN: "secondary", 
  WORLDVIA_HQ: "default",
  WORLDVIA_ADVISOR: "outline",
  CLIENT: "secondary",
  PUBLIC_PRO_USER: "default", 
  PUBLIC_FREE_USER: "outline"
};

// Available roles for the role selector (legacy)
const AVAILABLE_ROLES = [
  { value: "SYSTEM_ADMIN", label: "System Admin" },
  { value: "ADMIN", label: "Admin" },
  { value: "WORLDVIA_HQ", label: "WorldVia HQ" },
  { value: "WORLDVIA_ADVISOR", label: "WorldVia Advisor" },
  { value: "CLIENT", label: "Client" },
  { value: "PUBLIC_PRO_USER", label: "Pro User" },
  { value: "PUBLIC_FREE_USER", label: "Free User" },
];

export default function UserManagement() {
  const { toast } = useToast();
  const { isEnabled: isResolvedEnabled } = useResolvedFeatureFlags();
  const { user: currentUser } = useAuth();
  const { startImpersonation, isLoading: isImpersonationLoading } = useImpersonation();
  const [, navigate] = useLocation();
  const [viewingUser, setViewingUser] = useState<AdminUser | null>(null);
  const [roleAccessUser, setRoleAccessUser] = useState<AdminUser | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Track previous search to detect changes and reset page synchronously
  const prevSearchRef = useRef(debouncedSearch);
  const searchChanged = prevSearchRef.current !== debouncedSearch;
  
  // Compute effective page: reset to 1 when search changes, otherwise use current page
  const effectivePage = searchChanged ? 1 : page;
  
  // Sync the page state after render if search changed
  useEffect(() => {
    if (searchChanged) {
      prevSearchRef.current = debouncedSearch;
      setPage(1);
    }
  }, [debouncedSearch, searchChanged]);

  // Build query params with effective page (avoids double API call)
  const queryParams = new URLSearchParams({
    page: String(effectivePage),
    limit: String(limit),
  });
  if (debouncedSearch.trim()) {
    queryParams.set("search", debouncedSearch.trim());
  }

  // Fetch users - API returns { users: [...], total, page, limit, totalPages }
  const { data, isLoading, error } = useQuery({
    queryKey: ["/api/admin/users", effectivePage, limit, debouncedSearch],
    queryFn: (): Promise<UsersApiResponse> => apiRequest("GET", `/api/admin/users?${queryParams.toString()}`),
    placeholderData: keepPreviousData,
  });
  const users = (data as UsersApiResponse | undefined)?.users ?? [];
  const totalCount = (data as UsersApiResponse | undefined)?.total ?? users.length;

  const totalPages = useMemo(() => {
    const typedData = data as UsersApiResponse | undefined;
    if (!typedData) return 1;
    if (typedData.totalPages) return Math.max(1, typedData.totalPages);
    const derived = Math.ceil((typedData.total ?? 0) / (typedData.limit || limit));
    return Math.max(1, derived || 1);
  }, [data, limit]);

  useEffect(() => {
    if (effectivePage > totalPages) {
      setPage(totalPages);
    }
  }, [effectivePage, totalPages]);

  // Update user status mutation (role, advancedMode, isActive)
  // Note: queryContext captures page/search state at mutation time to ensure correct cache update
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, updates }: { 
      id: number; 
      updates: { role?: string; advancedMode?: boolean; isActive?: boolean };
      queryContext: { page: number; search: string };
    }) => {
      return await apiRequest("PATCH", `/api/admin/users/${id}/status`, updates);
    },
    onSuccess: async (data, variables) => {
      const { updates, queryContext } = variables;
      // Try to fetch authoritative RBAC user detail so we always have primaryRole after update
      let rbacDetail: any = null;
      try {
        rbacDetail = await apiRequest("GET", `/api/admin/rbac/users/${variables.id}`);
      } catch {
        // ignore; fall back to mutation response + submitted updates
      }

      const roleFromResponse =
        rbacDetail?.primaryRole?.slug ??
        (rbacDetail as any)?.roleSlug ??
        (data as any)?.user?.roleSlug ??
        (data as any)?.user?.role ??
        (data as any)?.roleSlug ??
        (data as any)?.role ??
        (data as any)?.user?.primaryRole?.slug ??
        updates.role;
      const advancedFromResponse =
        rbacDetail?.threadsAdvancedEnabled ??
        (data as any)?.user?.threadsAdvancedEnabled ??
        (data as any)?.threadsAdvancedEnabled ??
        (data as any)?.user?.threadsAdvanced ??
        updates.advancedMode;
      const isActiveFromResponse =
        rbacDetail?.isActive ??
        (data as any)?.user?.isActive ??
        (data as any)?.isActive ??
        updates.isActive;

      // Patch the cache entry for the page/search state when mutation was initiated
      // This provides immediate feedback if user is still on the same page
      queryClient.setQueryData<UsersApiResponse>(["/api/admin/users", queryContext.page, limit, queryContext.search], (prev) => {
        if (!prev?.users) return prev;
        const updatedUsers = prev.users.map((u) =>
          u.id === variables.id
            ? {
                ...u,
                roleSlug: roleFromResponse ?? u.roleSlug ?? u.role,
                role: roleFromResponse ?? u.role,
                threadsAdvancedEnabled:
                  advancedFromResponse !== undefined ? advancedFromResponse : u.threadsAdvancedEnabled,
                isActive: isActiveFromResponse !== undefined ? isActiveFromResponse : u.isActive,
              }
            : u,
        );
        return { ...prev, users: updatedUsers };
      });

      // Invalidate all user queries to ensure consistency across all cached pages/searches
      // This handles the case where user navigated to a different page/search during the mutation
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });

      toast({
        title: "Success",
        description: "User updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              Error loading users: {(error as Error).message}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          {/* AR-210 (TC-36.3): AdminLayout already renders the page h1 'User Management'. */}
          <p className="text-3xl font-bold tracking-tight">User Management</p>
          <p className="text-muted-foreground">
            Manage user roles, access permissions, and advanced mode settings
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                All Users ({totalCount})
              </CardTitle>
              {/* AR-210 (TC-37.2): the count change after a search was not announced. */}
              <p className="sr-only" role="status" aria-live="polite">
                {`${totalCount} user${totalCount === 1 ? '' : 's'}`}
              </p>
              <CardDescription>
                View and manage user roles, module access, and advanced mode settings
              </CardDescription>
            </div>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                aria-label="Search users by name or email"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* AR-210 (TC-40.1, AR-367): below 1440px the secondary columns step out so the
              table fits its column instead of scrolling sideways (416px at 1024px).
              Team and Created return at 1440px, Threads Tools and Subscription at
              1280px; every value is still in View Details. */}
          <div className="rounded-md border [&_table]:!min-w-[440px] min-[1280px]:[&_table]:!min-w-[640px] min-[1440px]:[&_table]:!min-w-[860px]">
            {/* Fixed layout + explicit widths: on wide screens the table fits the
                card with no horizontal scroll (User absorbs the remainder and
                truncates long emails); below the min width the wrapper scrolls. */}
            <Table aria-label="Users" className="table-fixed min-w-[860px] [&_th]:px-2 [&_td]:px-2">
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="w-[13%] hidden min-[1440px]:table-cell">Team</TableHead>
                  <TableHead className="w-[152px]">Role</TableHead>
                  <TableHead className="w-[108px] hidden min-[1280px]:table-cell">Threads Tools</TableHead>
                  <TableHead className="w-[92px] hidden min-[1280px]:table-cell">Subscription</TableHead>
                  <TableHead className="w-[72px]">Status</TableHead>
                  <TableHead className="w-[100px] hidden min-[1440px]:table-cell">Created</TableHead>
                  <TableHead className="w-[56px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Search className="h-8 w-8 opacity-50" />
                        {debouncedSearch ? (
                          <>
                            <span>No users found matching "{debouncedSearch}"</span>
                            <Button variant="link" size="sm" onClick={() => setSearchQuery("")}>
                              Clear search
                            </Button>
                          </>
                        ) : (
                          <span>No users found</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user: AdminUser) => (
                    <UserRow 
                      key={user.id} 
                      user={user} 
                      onRoleChange={(role) => updateStatusMutation.mutate({ id: user.id, updates: { role }, queryContext: { page: effectivePage, search: debouncedSearch } })}
                      onAdvancedModeChange={(advancedMode) => updateStatusMutation.mutate({ id: user.id, updates: { advancedMode }, queryContext: { page: effectivePage, search: debouncedSearch } })}
                      onStatusChange={(isActive) => updateStatusMutation.mutate({ id: user.id, updates: { isActive }, queryContext: { page: effectivePage, search: debouncedSearch } })}
                      showEditUser={isResolvedEnabled('onboarding')}
                      onEditUser={() => navigate(`/admin/users/${user.id}`)}
                      onViewDetails={() => setViewingUser(user)}
                      onManageAccess={() => setRoleAccessUser(user)}
                      showViewAsMember={!isImpersonationLoading && isResolvedEnabled('invoicingMvp') && user.isActive && user.id !== currentUser?.id}
                      onViewAsMember={async () => {
                        if (isImpersonationLoading) return;
                        try {
                          await startImpersonation(user.id);
                          navigate(FINANCES_PATHS.invoices);
                          toast({
                            title: 'Impersonation started',
                            description: `Now viewing Invoice Tool as ${user.displayName}`,
                          });
                        } catch (err) {
                          toast({
                            title: 'Error',
                            description: err instanceof Error ? err.message : 'Failed to start impersonation',
                            variant: 'destructive',
                          });
                        }
                      }}
                      isUpdating={updateStatusMutation.isPending}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {effectivePage} of {totalPages} · Showing {Math.min((effectivePage - 1) * limit + 1, totalCount)}-
              {Math.min(effectivePage * limit, totalCount)} of {totalCount}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={effectivePage <= 1 || isLoading}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={effectivePage >= totalPages || isLoading}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View User Dialog */}
      <Dialog open={!!viewingUser} onOpenChange={(open) => !open && setViewingUser(null)}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              View detailed information about this user.
            </DialogDescription>
          </DialogHeader>
          {viewingUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Display Name</label>
                  <p className="text-sm">{viewingUser.displayName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <p className="text-sm">{viewingUser.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Role</label>
                  <div className="text-sm mt-1">
                    <Badge variant={roleBadgeVariants[viewingUser.roleSlug ?? viewingUser.role] || "outline"}>
                      {roleLabels[viewingUser.roleSlug ?? viewingUser.role] || viewingUser.roleSlug || viewingUser.role}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Team</label>
                  <p className="text-sm mt-1">{viewingUser.companyName?.trim() || '—'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="text-sm mt-1">
                    <Badge variant={viewingUser.isActive ? "default" : "secondary"}>
                      {viewingUser.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Subscription</label>
                  <div className="text-sm mt-1">
                    <Badge variant={viewingUser.subscriptionTier === 'PRO' ? "default" : "secondary"}>
                      {viewingUser.subscriptionTier === 'PRO' ? 'Pro' : 'Free'}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Threads Tools</label>
                  <div className="text-sm mt-1">
                    <Badge variant={viewingUser.threadsAdvancedEnabled ? "default" : "secondary"}>
                      {viewingUser.threadsAdvancedEnabled ? "Advanced" : "Standard"}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
                  <p className="text-sm">{format(new Date(viewingUser.createdAt), "PPP")}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                  <p className="text-sm">{format(new Date(viewingUser.updatedAt), "PPP")}</p>
                </div>
              </div>

              {/* TRAMS Accounts Management — gated by invoicingMvp */}
              {isResolvedEnabled('invoicingMvp') && (
                <TramsAccountManager
                  key={viewingUser.id}
                  userId={viewingUser.id}
                  userDisplayName={viewingUser.displayName}
                />
              )}

              {/* View As Member — gated by invoicingMvp, only for active users that are not the current admin */}
              {isResolvedEnabled('invoicingMvp') &&
                viewingUser.isActive &&
                viewingUser.id !== currentUser?.id && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      disabled={isImpersonationLoading}
                      onClick={async () => {
                        try {
                          await startImpersonation(viewingUser.id);
                          setViewingUser(null);
                          navigate(FINANCES_PATHS.invoices);
                          toast({
                            title: 'Impersonation started',
                            description: `Now viewing Invoice Tool as ${viewingUser.displayName}`,
                          });
                        } catch (err) {
                          toast({
                            title: 'Error',
                            description: err instanceof Error ? err.message : 'Failed to start impersonation',
                            variant: 'destructive',
                          });
                        }
                      }}
                    >
                      {isImpersonationLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <UserCheck className="h-4 w-4" />
                      )}
                      View As Member (Invoice Tool)
                    </Button>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Role & Access Sheet */}
      <RoleAccessSheet
        userId={roleAccessUser?.id ?? null}
        userName={roleAccessUser?.displayName ?? ''}
        userEmail={roleAccessUser?.email ?? ''}
        legacyRole={roleAccessUser?.roleSlug ?? roleAccessUser?.role ?? null}
        open={!!roleAccessUser}
        onOpenChange={(open) => !open && setRoleAccessUser(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({
            predicate: (q) =>
              Array.isArray(q.queryKey) && q.queryKey[0] === '/api/admin/users',
          });
        }}
      />
    </div>
  );
}

interface UserRowProps {
  user: AdminUser;
  onRoleChange: (role: string) => void;
  onAdvancedModeChange: (advancedMode: boolean) => void;
  onStatusChange: (isActive: boolean) => void;
  /** Hidden while the `onboarding` flag is off — the editor lives behind it. */
  showEditUser?: boolean;
  onEditUser: () => void;
  onViewDetails: () => void;
  onManageAccess: () => void;
  onViewAsMember?: () => void;
  showViewAsMember?: boolean;
  isUpdating: boolean;
}

function UserRow({ 
  user, 
  onRoleChange, 
  onAdvancedModeChange, 
  onStatusChange, 
  showEditUser,
  onEditUser,
  onViewDetails, 
  onManageAccess,
  onViewAsMember,
  showViewAsMember,
  isUpdating 
}: UserRowProps) {
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);

  // Use roleSlug if available, fallback to legacy role
  const displayRole =
    (user as any).primaryRoleSlug ??
    (user as any).primaryRole?.slug ??
    user.roleSlug ??
    user.role;
  
  return (
    <TableRow>
      <TableCell>
        <div className="min-w-0 space-y-1">
          <div className="font-medium truncate" title={user.displayName}>{user.displayName}</div>
          <div className="text-sm text-muted-foreground truncate" title={user.email}>{user.email}</div>
        </div>
      </TableCell>
      <TableCell className="hidden min-[1440px]:table-cell">
        {user.companyName ? (
          <span className="block text-sm truncate" title={user.companyName}>{user.companyName}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={roleBadgeVariants[displayRole] || "outline"} className="whitespace-nowrap">
          <Shield className="h-3 w-3 mr-1" />
          {roleLabels[displayRole] || displayRole}
        </Badge>
      </TableCell>
      <TableCell className="hidden min-[1280px]:table-cell">
        <Badge 
          variant={user.threadsAdvancedEnabled ? "default" : "secondary"}
          className="gap-1 whitespace-nowrap"
        >
          <Zap className="h-3 w-3" />
          {user.threadsAdvancedEnabled ? "Advanced" : "Standard"}
        </Badge>
      </TableCell>
      <TableCell className="hidden min-[1280px]:table-cell">
        <Badge variant={user.subscriptionTier === 'PRO' ? "default" : "secondary"}>
          {user.subscriptionTier === 'PRO' ? 'Pro' : 'Free'}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={user.isActive ? "default" : "secondary"}>
          {user.isActive ? "Active" : "Inactive"}
        </Badge>
      </TableCell>
      <TableCell className="hidden min-[1440px]:table-cell">
        <div className="text-sm whitespace-nowrap">
          {format(new Date(user.createdAt), "MMM d, yyyy")}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" aria-label={`Actions for ${user.displayName}`}>
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {showEditUser && (
              <DropdownMenuItem onClick={onEditUser}>
                <SquarePen className="h-4 w-4 mr-2" />
                Edit User
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onManageAccess}>
              <Settings2 className="h-4 w-4 mr-2" />
              Manage Access
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onViewDetails}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {showViewAsMember && (
              <DropdownMenuItem onClick={onViewAsMember}>
                <UserCheck className="h-4 w-4 mr-2" />
                View As Member
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {user.isActive ? (
              <DropdownMenuItem onSelect={() => setDeactivateConfirmOpen(true)}>
                <ToggleLeft className="h-4 w-4 mr-2" />
                Deactivate
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => setActivateConfirmOpen(true)}>
                <ToggleLeft className="h-4 w-4 mr-2" />
                Activate
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <AlertDialog open={activateConfirmOpen} onOpenChange={setActivateConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Activate user?</AlertDialogTitle>
              <AlertDialogDescription>
                This will reactivate <strong>{user.displayName}</strong> ({user.email}).
                They will regain access to the platform based on their assigned role.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={isUpdating}
                onClick={() => onStatusChange(true)}
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Activating...
                  </>
                ) : (
                  "Activate"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deactivateConfirmOpen} onOpenChange={setDeactivateConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate user?</AlertDialogTitle>
              <AlertDialogDescription>
                This will deactivate <strong>{user.displayName}</strong> ({user.email}).
                They will lose access to the platform until reactivated.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isUpdating}
                onClick={() => onStatusChange(false)}
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deactivating...
                  </>
                ) : (
                  "Deactivate"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
}
